/**
 * Feeds telehealth participant audio to the Ambient Listening panel in the EMR
 * that opened this app.
 *
 * The EMR page has an equivalent bridge for the in-page telehealth session,
 * where it can tap the Twilio Room directly. It cannot do that here: this app
 * runs in a popup on its own origin, so the Room object never reaches it. The
 * tap therefore runs on this side and only the audio crosses, as PCM --
 * MediaStreamTrack is not a Transferable type in any browser, ArrayBuffer is.
 *
 * Every subscribed remote participant is summed into one 16kHz AudioContext
 * (Web Audio mixes connected sources for free) and flushed as ~250ms Int16
 * chunks. The EMR page relays those into the AL iframe.
 *
 * The trackId sent across is a fixed constant, never a Twilio
 * participant.identity -- identity carries unique_name/PatientId.
 *
 * The clinician's own mic track goes across too, as a second stream: AL records
 * the clinician from it in place of its own mic, so muting here mutes AL.
 */
import { useEffect, useRef, useState } from 'react';
import {
  LocalAudioTrack,
  LocalTrackPublication,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrack,
  Room,
} from 'twilio-video';

const SAMPLE_RATE = 16000;
const TRACK_ID = 'telehealth-remote';
const LOCAL_TRACK_ID = 'telehealth-local';
const WORKLET_URL = '/audio-worklets/pcm-capture-processor.js';
const WORKLET_NAME = 'pcm-capture-processor';

/**
 * EMR origins allowed to receive participant audio. Baked in at image build
 * time by the pipeline, like the AL UI's VITE_ALLOWED_HOST_ORIGINS. Unset means
 * no origin is trusted and nothing is captured -- a wrong value can only mean
 * no capture, never a wrong recipient.
 */
export function allowedEmrOrigins(): string[] {
  return String(process.env.REACT_APP_ALLOWED_EMR_ORIGINS ?? '')
    .split(',')
    .map(origin => origin.trim())
    .filter(Boolean);
}

/**
 * Candidate EMR windows. window.open() is called from the appointments-grid
 * iframe, so the opener itself is usually not the page hosting the AL panel --
 * its ancestors are candidates too. `parent`/`top` are readable cross-origin.
 * Wrong targets are harmless: postMessage drops anything whose targetOrigin
 * does not match the receiver.
 *
 * Remembered in `seen`, not just re-read: that grid iframe lives in a modal
 * dialog the clinician has to close to reach the AL panel, and closing it
 * destroys the iframe, which nulls window.opener. The encounter page hosting
 * the relay outlives it, so a late al:ready and the call-end announce must
 * still reach it. A window stops counting once it reports `closed`, which is
 * final, so it is filtered out on read rather than removed from `seen`. `seen`
 * holds at most the three windows first seen through the opener.
 */
function hostWindows(seen: Window[]): Window[] {
  // Spec says `opener`/`parent`/`top`/`closed` are readable cross-origin, so
  // this should never throw. Guarded anyway: both callers run from paths with no try/catch
  // of their own (the worklet message callback and the window message
  // listener), and losing capture must never take the call down with it.
  try {
    const opener: Window | null = window.opener;
    if (opener) {
      [opener, opener.parent, opener.top].forEach(win => {
        if (win && !seen.includes(win)) seen.push(win);
      });
    }
    return seen.filter(win => !win.closed);
  } catch {
    return []; // an unreadable chain fails closed, remembered windows included
  }
}

export interface HostChannel {
  /**
   * Posts to every candidate window/origin pair. Control messages only -- no
   * transfers, and plain structured-cloneable objects only: a message carrying
   * something uncloneable would throw per post and be swallowed by the catch
   * below, so it would simply never arrive. Anything needing a transfer goes
   * through sendChunk(). The fan-out is deliberate and bounded at 3 windows x N origins:
   * we do not know which ancestor hosts the AL panel until it answers, and
   * postMessage drops anything whose targetOrigin does not match. PCM chunks do
   * NOT take this path -- they go to the single pinned host via sendChunk().
   *
   * Still a full fan-out after accept() has pinned a host, deliberately: the
   * pinned window can go away mid-call, and sendChunk()'s recovery re-announces
   * through here to let another ancestor answer al:ready.
   *
   * The allowlist is meant to hold one entry, or two across a cutover -- it is
   * not a place to accumulate environments, since every entry multiplies the
   * posts each control message costs.
   *
   * A multi-origin allowlist (staging + prod configured together) therefore
   * costs extra posts, not extra messages -- the browser delivers only the
   * entry matching the receiver's real origin and drops the rest. Distinct
   * windows in the chain can each get one, and every type sent here is
   * idempotent on arrival: a repeated call-active sets the same boolean in AL,
   * a second stream-ended finds no track and returns, and unsupported
   * re-renders the same warning text.
   */
  broadcast(message: object): void;
  /** True when the event is the EMR's 'al:ready'; pins that window as the chunk target. */
  accept(event: MessageEvent): boolean;
  /**
   * Ships one PCM chunk to the pinned host -- the participant mix, or with
   * `local` the clinician's own track. No-op until accept() has succeeded.
   */
  sendChunk(buffer: ArrayBuffer, local?: boolean): void;
  /**
   * The document is unloading: accept() refuses and sendChunk() is a no-op from
   * here on. broadcast() still goes out, so the call-end reaches the EMR.
   */
  close(): void;
}

export function createHostChannel(): HostChannel {
  const origins = allowedEmrOrigins();
  let host: { win: Window; origin: string } | null = null;
  let sequence = 0;
  let localSequence = 0; // per stream: AL drops chunks whose sequence goes backwards
  // Issued by the EMR page with its al:ready and echoed on everything we send,
  // so that page can tell this call's document from a previous call's still
  // running in the same reused popup window.
  //
  // First-wins, deliberately. A *later* EMR page answering our announce must
  // not be able to re-stamp this document with its own token: a call still
  // running when the EMR tab moves to another patient would otherwise be
  // re-tokened by that patient's page and its audio accepted there (bug 76694).
  let captureToken: string | null = null;
  let closed = false;

  const stamp = (message: object): object => (captureToken === null ? message : { ...message, alToken: captureToken });

  // null until the first broadcast; then tracks whether the previous attempt had
  // any target, so a mid-call loss is reported and a continuously-empty chain is
  // not logged on every message.
  let hadTargets: boolean | null = null;
  // One re-announce is allowed after losing the host, so a transient failure is
  // recoverable without bounding it by the ~4/sec chunk rate.
  let reannounced = false;
  // Seeded now, at App mount, while the opener is still certain to be alive.
  const seen: Window[] = [];
  hostWindows(seen);

  const broadcast = (message: object): void => {
    const targets = hostWindows(seen);
    // Zero targets means total, silent capture failure -- the same outcome as
    // an unset allowlist, which does warn. Opening the telehealth URL in a
    // plain tab rather than via window.open is the easy way to hit it from a
    // cold start; losing every EMR window mid-call (the EMR tab closes, COOP
    // isolation kicks in) is the same failure arriving later, so warn on that
    // transition too rather than only on the first attempt. An EMR tab that
    // navigates away is not caught here: its window stays open, and the new
    // page's relay simply drops what arrives.
    if (targets.length === 0 && hadTargets !== false) {
      console.warn(
        hadTargets === true
          ? 'AL telehealth bridge: lost the EMR window mid-call — participant audio is no longer being captured'
          : 'AL telehealth bridge: no EMR window found (window.opener is null) — participant audio will not be captured'
      );
    }
    hadTargets = targets.length > 0;
    const stamped = stamp(message);
    targets.forEach(win =>
      origins.forEach(origin => {
        try {
          win.postMessage(stamped, origin);
        } catch {
          // Window closed between resolving it and posting. Nothing to do:
          // if the EMR page is gone there is no AL panel to feed.
        }
      })
    );
  };

  return {
    broadcast,

    accept(event) {
      if (closed) return false;
      if (!origins.includes(event.origin)) return false;
      // Origin alone only proves an allowlisted page sent this, not that it is
      // the page that opened us. Mirrors the source check on the EMR side.
      if (!event.source || !hostWindows(seen).includes(event.source as Window)) return false;
      if (!event.data || (event.data as { type?: unknown }).type !== 'al:ready') return false;
      // First-wins; see captureToken above.
      const issued = (event.data as { alToken?: unknown }).alToken;
      if (captureToken === null && typeof issued === 'string' && issued !== '') {
        captureToken = issued;
      }
      // Last valid sender wins -- reopening the AL panel replays al:ready.
      //
      // The threat model this rests on, recorded so it does not have to be
      // re-derived: re-pinning requires script already running in a window
      // that is both on an allowlisted EMR origin and a still-open window that
      // was our opener/parent/top. The checks above keep the set that narrow;
      // they are the control, not this ordering rule. They do not prove the
      // sender holds this call: a remembered top window may since have
      // navigated to another EMR page, even another patient's encounter. What
      // bounds that is the EMR relay, which sends al:ready only to the popup its
      // own page opened, or after a reload of that same page to the popup
      // presenting the token it issued. A different page reaches us only by
      // relaunching telehealth, which reloads this popup; it mints a new token,
      // so anything we send under ours during the unload -- chunks in flight
      // included -- is dropped there.
      // Moving to a *different* window while one is still pinned redirects
      // capture away from the panel that was receiving it, so it warns rather
      // than informs, matching the level used for the other capture-affecting
      // conditions here. Recovery does not trip this: sendChunk() clears `host`
      // before re-announcing, so a re-pin after a loss is not a redirect.
      // Window identity only, no content.
      if (host && host.win !== event.source) {
        console.warn('AL telehealth bridge: re-pinned to a different EMR window');
      }
      host = { win: event.source as Window, origin: event.origin };
      reannounced = false; // recovered; allow a future re-announce
      return true;
    },

    sendChunk(buffer, local = false) {
      if (!host) return; // close() nulls host, and accept() refuses to re-pin once closed
      try {
        // Exactly one target: transferring the same ArrayBuffer twice would
        // throw on the second post, since the first detaches it. The only
        // callers are the two worklets' port.onmessage, each delivering each
        // buffer once -- a call site handing over the same buffer twice would
        // reintroduce that double-transfer.
        host.win.postMessage(
          stamp(
            local
              ? { type: 'al:telehealth-local-pcm-chunk', trackId: LOCAL_TRACK_ID, sequence: localSequence, buffer }
              : { type: 'al:telehealth-audio-pcm-chunk', trackId: TRACK_ID, sequence, buffer }
          ),
          host.origin,
          [buffer]
        );
        if (local) localSequence += 1;
        else sequence += 1;
      } catch {
        // The host window closed or navigated since accept(). This runs inside
        // the AudioWorklet port callback, where an escaping exception surfaces
        // as an unhandled error in the call UI -- a far worse outcome than
        // losing capture. Unpin so later chunks stop retrying a dead window.
        host = null;
        // Without this, one failed post ends capture for the rest of the call.
        // hostWindows() re-resolves, so if another ancestor is still alive and
        // still armed it can answer al:ready and accept() re-pins. Once only:
        // chunks arrive ~4/sec and this must not become a broadcast loop.
        if (!reannounced) {
          reannounced = true;
          broadcast({ type: 'al:telehealth-call-active', active: true });
        }
      }
    },

    close() {
      closed = true;
      host = null;
    },
  };
}

export default function useALAudioBridge(room: Room | null) {
  const channelRef = useRef<HostChannel | null>(null);
  if (channelRef.current === null) channelRef.current = createHostChannel();
  const channel = channelRef.current;

  const [ready, setReady] = useState(false);

  // The message handler below needs the current room, but must not take `room`
  // as a dependency: that would unregister and re-register the listener on room
  // join, and al:ready is delivered exactly once. React flushes effect cleanup
  // and setup in one synchronous pass, so nothing can actually arrive in the
  // gap -- a ref keeps it that way without depending on that argument holding.
  const roomRef = useRef(room);
  roomRef.current = room;

  // Registered on mount rather than on room join: the clinician can open the AL
  // panel at any point, and its 'al:ready' is answered to us once. Missing it
  // would silently withhold participant audio for the whole call.
  useEffect(() => {
    if (allowedEmrOrigins().length === 0) {
      console.warn(
        'AL telehealth bridge: REACT_APP_ALLOWED_EMR_ORIGINS is not configured — participant audio will not be captured'
      );
      return;
    }
    const onMessage = (event: MessageEvent) => {
      if (!channel.accept(event)) return;
      setReady(true);
      // The EMR answers al:ready with a call-active of its own, derived from a
      // Room that is always null in this shape -- so opening the AL panel
      // mid-call tells AL there is no call. The announce effect below already
      // ran while the panel was closed and will not run again, so re-announce
      // from the side that actually holds the Room. Without this AL keeps
      // telehealthCallActive false for the rest of the call and its
      // "recording one side only" warning can never fire.
      //
      // `reply: true` marks this as an answer to al:ready rather than a fresh
      // announce. The EMR answers every unmarked control message with al:ready,
      // and accept() succeeds every time, so without this marker the two rules
      // feed each other without bound. The marker is consumed in WebSites/PatagoniaEMR/js/ambient-listening/al-telehealth-audio-bridge.js.
      // It is transport-only: the EMR relay rebuilds relayed messages from a field allowlist, so it never
      // reaches AL.
      if (roomRef.current) {
        channel.broadcast({ type: 'al:telehealth-call-active', active: true, reply: true });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [channel]);

  // Announce the call. This is also what tells the EMR page which window we
  // are, so it can answer 'al:ready' here when the panel opens later.
  useEffect(() => {
    if (!room) return;
    channel.broadcast({ type: 'al:telehealth-call-active', active: true });

    const onEnded = () => channel.broadcast({ type: 'al:telehealth-call-active', active: false });
    // Unlike a Twilio disconnect, unloading cannot be followed by a rejoin in
    // this document, so it also closes the channel. A relaunch navigates this
    // same named window, and until that commits the new launch's al:ready can
    // still reach this document; adopting it would stream this call's audio
    // into the next patient's session (bug 76694).
    const onUnload = () => {
      onEnded();
      channel.close();
    };
    room.on('disconnected', onEnded);
    // beforeunload covers the clinician closing the popup mid-call, where the
    // Twilio 'disconnected' event does not always get a chance to fire.
    window.addEventListener('beforeunload', onUnload);
    return () => {
      room.off('disconnected', onEnded);
      window.removeEventListener('beforeunload', onUnload);
      onEnded();
    };
  }, [room, channel]);

  useEffect(() => {
    if (!room || !ready) return;

    const AudioCtor = window.AudioContext || (window as any).webkitAudioContext;
    const ctx: AudioContext = new AudioCtor({ sampleRate: SAMPLE_RATE });
    if (ctx.sampleRate !== SAMPLE_RATE) {
      // Browser refused the requested rate -- do not ship mis-rated PCM.
      channel.broadcast({ type: 'al:telehealth-audio-unsupported' });
      ctx.close();
      return;
    }

    const sources = new Map<string, MediaStreamAudioSourceNode>();
    let node: AudioWorkletNode | null = null;
    let disposed = false;

    const addTrack = (track: RemoteAudioTrack) => {
      if (!node || disposed || sources.has(track.sid) || !track.mediaStreamTrack) return;
      const source = ctx.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
      source.connect(node); // sums with any other already-connected participant
      sources.set(track.sid, source);
    };

    const removeTrack = (track: RemoteAudioTrack) => {
      const source = sources.get(track.sid);
      if (!source) return;
      source.disconnect();
      sources.delete(track.sid);
      if (sources.size === 0) {
        channel.broadcast({ type: 'al:telehealth-audio-stream-ended', trackId: TRACK_ID });
      }
    };

    // Participant-level, not per-publication: a publication handler would fire
    // twice for the same subscription and still miss later publications.
    //
    // Subscription events only. Mute ('trackDisabled') is deliberately not
    // handled: the underlying MediaStreamTrack stays connected and Web Audio
    // feeds silence, which is what the transcript should show. Disconnecting on
    // mute would mean re-wiring on unmute for no gain, and a missed 'trackEnabled'
    // would silently drop that participant for the rest of the call.
    const watch = (participant: RemoteParticipant) => {
      participant.audioTracks.forEach(publication => {
        if (publication.isSubscribed && publication.track) addTrack(publication.track);
      });
      participant.on('trackSubscribed', (track: RemoteTrack) => {
        if (track.kind === 'audio') addTrack(track);
      });
      participant.on('trackUnsubscribed', (track: RemoteTrack) => {
        if (track.kind === 'audio') removeTrack(track);
      });
    };

    const forget = (participant: RemoteParticipant) =>
      participant.audioTracks.forEach(publication => {
        if (publication.track) removeTrack(publication.track);
      });

    // The clinician's own track, on its own worklet so it stays a separate
    // stream. Mute is LocalAudioTrack.disable(), which feeds silence here -- so
    // chunks keep flowing and AL keeps its mic off. Built only once a local
    // audio track exists: with none, nothing is sent and AL keeps its mic.
    let localNode: AudioWorkletNode | null = null;
    let localSource: MediaStreamAudioSourceNode | null = null;
    let tappedLocal: LocalAudioTrack | null = null;
    const watchedLocal = new Set<LocalAudioTrack>();
    const localCleanups: (() => void)[] = [];

    // Untapping is what stops the chunks: a stopped (ended) track left
    // connected keeps feeding zeros in Chromium, which would hold AL's mic off
    // with nothing behind it. With no input the worklet posts nothing, and AL
    // falls back to its mic after a short silence.
    const untapLocal = () => {
      localSource?.disconnect();
      localSource = null;
      tappedLocal = null;
    };
    const tapLocal = (track: LocalAudioTrack) => {
      untapLocal();
      if (disposed || !track.mediaStreamTrack) return;
      if (!localNode) {
        localNode = new AudioWorkletNode(ctx, WORKLET_NAME);
        localNode.port.onmessage = (event: MessageEvent) => channel.sendChunk(event.data as ArrayBuffer, true);
      }
      localSource = ctx.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
      localSource.connect(localNode);
      tappedLocal = track;
    };
    const watchLocal = (track: LocalAudioTrack) => {
      if (watchedLocal.has(track)) return;
      watchedLocal.add(track);
      // restart() -- a device switch -- emits 'stopped', swaps mediaStreamTrack,
      // then emits 'started'.
      const onStarted = () => tapLocal(track);
      // Only the tapped track may untap: a late stop from one we have moved
      // off would otherwise cut off the live one for the rest of the call.
      const onStopped = () => {
        if (tappedLocal === track) untapLocal();
      };
      track.on('started', onStarted);
      track.on('stopped', onStopped);
      localCleanups.push(() => {
        track.off('started', onStarted);
        track.off('stopped', onStopped);
      });
      tapLocal(track);
    };
    // No 'trackUnpublished' handling: this app never unpublishes its audio
    // mid-call -- device switches restart() it and mute disable()s it. A
    // mute-by-unpublish would need an untap there, or the track keeps reaching AL.
    const onLocalPublished = (publication: LocalTrackPublication) => {
      if (publication.kind === 'audio') watchLocal(publication.track as LocalAudioTrack);
    };

    // Participants are only wired up once the worklet exists, so unlike the EMR
    // bridge there is no in-flight-graph window to guard against.
    ctx.audioWorklet
      .addModule(WORKLET_URL)
      .then(() => {
        if (disposed) return;
        node = new AudioWorkletNode(ctx, WORKLET_NAME);
        node.port.onmessage = (event: MessageEvent) => channel.sendChunk(event.data as ArrayBuffer);
        room.participants.forEach(watch);
        room.on('participantConnected', watch);
        room.on('participantDisconnected', forget);
        room.localParticipant.audioTracks.forEach(publication => watchLocal(publication.track));
        room.localParticipant.on('trackPublished', onLocalPublished);
      })
      .catch(error => {
        if (disposed) return;
        // The clinician-facing warning in AL is generic, so without this the
        // actual cause is invisible from the console.
        console.warn('AL telehealth bridge: audio worklet failed to load from ' + WORKLET_URL, error);
        channel.broadcast({ type: 'al:telehealth-audio-unsupported' });
        // No graph will ever be built on this context. Relying on the effect's
        // cleanup would hold it open for the rest of the call, since a call that
        // continues without capture never unmounts this hook.
        ctx.close();
      });

    return () => {
      disposed = true;
      room.off('participantConnected', watch);
      room.off('participantDisconnected', forget);
      room.localParticipant.off('trackPublished', onLocalPublished);
      localCleanups.forEach(off => off());
      untapLocal();
      if (localNode) {
        localNode.port.onmessage = null;
        localNode.disconnect();
      }
      if (sources.size > 0) {
        channel.broadcast({ type: 'al:telehealth-audio-stream-ended', trackId: TRACK_ID });
      }
      sources.forEach(source => source.disconnect());
      sources.clear();
      // Detach the worklet callback before closing: ctx.close() would collect
      // the node anyway, but a queued port message delivered during teardown
      // would otherwise call sendChunk() on a disposed graph.
      if (node) {
        node.port.onmessage = null;
        node.disconnect();
      }
      // The worklet-failure path already closed it; close() on a closed context
      // rejects, and an unhandled rejection during teardown is exactly the noise
      // this hook is not allowed to add to the call.
      if (ctx.state !== 'closed') {
        ctx.close();
      }
    };
  }, [room, ready, channel]);
}
